import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type {
  AdministrativeAreaOption,
  ExchangeLocation,
  ExchangeLocationAssignment,
  ExchangeLocationType,
  TranslationFunction,
} from "../../types";
import {
  findAdministrativeAreaLabel,
  getExchangeLocationTypeLabel,
  isExchangeLocationTypeSelected,
  sortExchangeLocationTypes,
  toggleExchangeLocationTypeAssignment,
} from "../../utils";

interface ExchangeLocationAssignmentsTableProps {
  administrativeAreas: AdministrativeAreaOption[];
  exchangeLocations: ExchangeLocation[];
  exchangeLocationTypes: ExchangeLocationType[];
  value?: ExchangeLocationAssignment[];
  onChange: (value: ExchangeLocationAssignment[]) => void;
  t: TranslationFunction;
}

export const ExchangeLocationAssignmentsTable = ({
  administrativeAreas,
  exchangeLocations,
  exchangeLocationTypes,
  value = [],
  onChange,
  t,
}: ExchangeLocationAssignmentsTableProps) => {
  const sortedTypes = sortExchangeLocationTypes(exchangeLocationTypes);
  const columnCount = 3 + sortedTypes.length;

  const handleCheckedChange = (exchangeLocation: ExchangeLocation, type: ExchangeLocationType) => {
    onChange(toggleExchangeLocationTypeAssignment(value, exchangeLocation.id, type.id));
  };

  return (
    <div className="h-[27rem] overflow-y-scroll rounded-md border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="sticky top-0 z-10 bg-background">
              {t("traderDetails.form.table.outletName")}
            </TableHead>
            <TableHead className="sticky top-0 z-10 bg-background">
              {t("traderDetails.form.table.location")}
            </TableHead>
            <TableHead className="sticky top-0 z-10 bg-background">
              {t("traderDetails.form.table.status")}
            </TableHead>
            {sortedTypes.map(type => (
              <TableHead key={type.id} className="sticky top-0 z-10 bg-background text-center">
                {getExchangeLocationTypeLabel(type)}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {exchangeLocations.length ? (
            exchangeLocations.map(exchangeLocation => (
              <TableRow key={exchangeLocation.id}>
                <TableCell className="font-medium text-foreground">
                  {exchangeLocation.name ?? "-"}
                </TableCell>
                <TableCell>
                  {findAdministrativeAreaLabel(administrativeAreas, exchangeLocation.location)}
                </TableCell>
                <TableCell>
                  <Badge variant={exchangeLocation.active ? "success" : "destructive"}>
                    {exchangeLocation.active ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                {sortedTypes.map(type => {
                  const checkboxId = `multi_checkbox_row_${exchangeLocation.id}_field_${type.id}`;

                  return (
                    <TableCell key={type.id} className="text-center">
                      <Checkbox
                        id={checkboxId}
                        name={checkboxId}
                        checked={isExchangeLocationTypeSelected(
                          value,
                          exchangeLocation.id,
                          type.id
                        )}
                        onCheckedChange={() => handleCheckedChange(exchangeLocation, type)}
                        aria-label={`${exchangeLocation.name ?? "Exchange location"} - ${getExchangeLocationTypeLabel(type)}`}
                      />
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columnCount} className="h-24 text-center text-muted-foreground">
                No exchange locations available
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export type { ExchangeLocationAssignmentsTableProps };
